import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import SubHeader from '../../../layout/components/SubHeader';
import verificationService from '../../../../services/verification.service';
import settingsService from '../../../../services/settings.service';
import { useEyeconxStore } from '../../../../store/eyeconxStore';
import { showError, showSuccess } from '../../../../utils/ecxSwal';
import './VerifyEmail.css';

function VerifyEmail() {
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm();

    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const navigate = useNavigate();
    const [clinicInfo, setClinicInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadClinicInfo = async () => {
            try {
                const res = await settingsService.getClinicById(clinicId);
                setClinicInfo(res.data);
            } catch (error) {
                console.error('Error loading clinic info:', error);
            } finally {
                setIsLoading(false);
            }
        };

        if (clinicId) {
            loadClinicInfo();
        }
    }, [clinicId]);

    const onSubmit = async (data) => {
        try {
            await verificationService.confirmEmailVerification(clinicId, data.code.trim());
            await showSuccess({
                title: 'Email Verified',
                text: 'Your clinic email has been verified successfully.',
            });
            navigate('/settings/clinic');
        } catch (error) {
            const message =
                error.response?.data?.message ||
                error.response?.data?.error ||
                'Unable to verify email. Please check the code and try again.';
            showError({
                title: 'Verification Failed',
                text: message,
            });
        }
    };

    const isAlreadyVerified = clinicInfo?.verification?.email === true;

    return (
        <div className="ecx-page verify-email-page ecx-page--full-width">
            <SubHeader
                title="Verify Clinic Email"
                description="Enter the 6-digit code sent to your clinic email address"
                icon="bi-envelope-check"
                containerClass="w-100 px-4"
            />

            <div className="ecx-page-body">
                <div className="verify-email-card card">
                    {isLoading ? (
                        <div className="verify-email-card__loading">
                            <span className="spinner-border spinner-border-sm me-2" role="status" />
                            Loading clinic information…
                        </div>
                    ) : isAlreadyVerified ? (
                        <div className="verify-email-card__success">
                            <span className="verify-email-card__success-icon">
                                <i className="bi bi-check-circle-fill" />
                            </span>
                            <h5>Email Already Verified</h5>
                            <p className="text-muted mb-3">
                                <strong>{clinicInfo?.email}</strong> is already verified for this clinic.
                            </p>
                            <Link to="/settings/clinic" className="btn btn-primary btn-sm">
                                Back to Clinic Settings
                            </Link>
                        </div>
                    ) : (
                        <>
                            <p className="verify-email-card__intro">
                                A verification code was sent to{' '}
                                <strong>{clinicInfo?.email || 'your clinic email'}</strong>.
                                Enter the code below to confirm ownership. Codes expire after 30 minutes.
                            </p>

                            <form onSubmit={handleSubmit(onSubmit)} className="verify-email-form">
                                <div className="verify-email-form__field">
                                    <label htmlFor="verification-code" className="form-label">
                                        Verification Code
                                    </label>
                                    <input
                                        id="verification-code"
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        maxLength={6}
                                        placeholder="000000"
                                        className={`form-control form-control-lg verify-email-form__code-input ${
                                            errors.code ? 'is-invalid' : ''
                                        }`}
                                        disabled={isSubmitting}
                                        {...register('code', {
                                            required: 'Verification code is required',
                                            pattern: {
                                                value: /^\d{6}$/,
                                                message: 'Enter the 6-digit code from your email',
                                            },
                                        })}
                                    />
                                    {errors.code && (
                                        <div className="invalid-feedback d-block">{errors.code.message}</div>
                                    )}
                                </div>

                                <div className="verify-email-form__actions">
                                    <Link to="/settings/clinic" className="btn btn-outline-secondary btn-sm">
                                        Cancel
                                    </Link>
                                    <button type="submit" className="btn btn-primary btn-sm" disabled={isSubmitting}>
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status" />
                                                Verifying…
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-shield-check me-1" />
                                                Verify Email
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default VerifyEmail;
